/**
 * userPageFiltersApi — per-user dashboard-level filter state persisted via backend.
 */
import { apiSlice } from './apiSlice'

export type PageFiltersValue = Record<string, any> | unknown[]
export type PageFiltersDoc = Record<string, PageFiltersValue>

export interface PageFiltersResponse {
  filters: PageFiltersDoc
}

export interface PageFiltersPutPayload {
  pageKey: string
  filters: PageFiltersValue
}

const userPageFiltersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserPageFilters: builder.query<PageFiltersDoc, void>({
      query: () => ({ url: '/user-preferences/page-filters' }),
      transformResponse: (res: PageFiltersResponse) => res?.filters ?? {},
      providesTags: ['UserPageFilters'],
    }),

    putUserPageFilters: builder.mutation<{ message: string }, PageFiltersPutPayload>({
      query: ({ pageKey, filters }) => ({
        url: `/user-preferences/page-filters/${encodeURIComponent(pageKey)}`,
        method: 'PUT',
        body: { filters },
      }),
      onQueryStarted: async ({ pageKey, filters }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userPageFiltersApi.util.updateQueryData('getUserPageFilters', undefined, (draft) => {
            draft[pageKey] = Array.isArray(filters) ? [...filters] : { ...filters }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    deleteUserPageFilters: builder.mutation<{ message: string }, string>({
      query: (pageKey) => ({
        url: `/user-preferences/page-filters/${encodeURIComponent(pageKey)}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (pageKey, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userPageFiltersApi.util.updateQueryData('getUserPageFilters', undefined, (draft) => {
            delete draft[pageKey]
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),
  }),
})

export const {
  useGetUserPageFiltersQuery,
  usePutUserPageFiltersMutation,
  useDeleteUserPageFiltersMutation,
} = userPageFiltersApi

export default userPageFiltersApi
